import { apiClient } from "@base/api/client";
import type {
  AvailableEndTimesResponse,
  AvailableStartTimesResponse,
  CreateMeetingReservationRequest,
  CreateMeetingReservationResponse,
  MeetingRoomsResponse,
} from "@features/reservation/model/types";

/**
 * GET /82office/resource/api/meeting?date=YYYY-MM-DD
 * 미팅룸 조회 (해당 날짜별 룸 및 예약 현황)
 * date: YYYY-MM-DD 형식
 */
export async function fetchMeetingRooms(date: string): Promise<MeetingRoomsResponse> {
  const { data } = await apiClient.get<MeetingRoomsResponse>("/82office/resource/api/meeting", {
    params: { date },
  });
  return data;
}

/**
 * GET /82office/resource/api/meeting/available-starttime?room_id=1&date=2026-02-09
 * 미팅룸 예약 시 선택 가능한 시작 시간 조회
 */
export async function fetchAvailableStartTimes(roomId: number, date: string): Promise<AvailableStartTimesResponse> {
  const { data } = await apiClient.get<AvailableStartTimesResponse>(
    "/82office/resource/api/meeting/available-starttime",
    {
      params: { room_id: roomId, date },
    },
  );
  return data;
}

/**
 * GET /82office/resource/api/meeting/available-endtime?room_id=&start_time=&date=
 * 미팅룸 예약 시 선택 가능한 종료 시간 조회
 */
export async function fetchAvailableEndTimes(
  roomId: number,
  date: string,
  startTime: string,
): Promise<AvailableEndTimesResponse> {
  const { data } = await apiClient.get<AvailableEndTimesResponse>("/82office/resource/api/meeting/available-endtime", {
    params: { room_id: roomId, date, start_time: startTime },
  });
  return data;
}

/**
 * POST /82office/resource/api/meeting
 * 미팅룸 예약 생성 (hosted_page 있으면 외부 결제 링크로 이동)
 */
export async function createMeetingReservation(
  body: CreateMeetingReservationRequest,
): Promise<CreateMeetingReservationResponse> {
  const { data } = await apiClient.post<CreateMeetingReservationResponse>("/82office/resource/api/meeting", body);
  return data;
}

/**
 * DELETE /82office/resource/api/meeting/:id
 * 미팅룸 예약 취소
 */
export async function deleteMeetingReservation(reservationId: number): Promise<void> {
  await apiClient.delete(`/82office/resource/api/meeting/${reservationId}`);
}

/*
 * TanStack Query 사용 예시:
 *
 * import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
 * import {
 *   fetchMeetingRooms,
 *   fetchAvailableEndTimes,
 *   createMeetingReservation,
 *   deleteMeetingReservation,
 * } from "@features/reservation/api/meeting";
 *
 * // 미팅룸 목록 (날짜별)
 * const { data } = useQuery({
 *   queryKey: ["meetingRooms", dateStr],
 *   queryFn: () => fetchMeetingRooms(dateStr),
 *   enabled: !!dateStr,
 * });
 *
 * // 선택 가능 종료 시간 (roomId, date, startTime 변경 시)
 * const { data: endTimes } = useQuery({
 *   queryKey: ["availableEndTimes", roomId, dateStr, startTime],
 *   queryFn: () => fetchAvailableEndTimes(roomId, dateStr, startTime),
 *   enabled: !!roomId && !!dateStr && !!startTime,
 * });
 *
 * // 예약 생성 (useMutation)
 * const queryClient = useQueryClient();
 * const createMutation = useMutation({
 *   mutationFn: createMeetingReservation,
 *   onSuccess: (data) => {
 *     queryClient.invalidateQueries({ queryKey: ["meetingRooms"] });
 *     if (data.hosted_page) window.location.href = data.hosted_page;
 *   },
 * });
 *
 * // 예약 취소 (useMutation)
 * const deleteMutation = useMutation({
 *   mutationFn: deleteMeetingReservation,
 *   onSuccess: () => {
 *     queryClient.invalidateQueries({ queryKey: ["meetingRooms"] });
 *     queryClient.invalidateQueries({ queryKey: ["reservations"] });
 *   },
 * });
 * deleteMutation.mutate(13);
 */
